import React, { useRef } from 'react'
import cx from 'classnames'
import { useMenuItem } from '@react-aria/menu'
import { FocusRing } from '@react-aria/focus'
import Styles from './Menu.module.scss'
import { ReactComponent as CheckIcon } from 'assets/icons/check16.svg'
import { ChevronRight } from 'almanac-ui/icons'
import { MenuItemProps } from './Types'
import { SubmenuTrigger } from 'components/MenuTrigger/SubmenuTrigger'

function SubmenuItem<T>({
  className,
  item,
  showCheckmark = true,
  state,
  onAction,
  onClose,
  closeOnSelect = true,
}: MenuItemProps<T>) {
  let { rendered, key } = item

  const isExpanded = state.expandedKeys.has(key)
  const isSelected = state.selectionManager.isSelected(key.toString())
  const isDisabled = state.disabledKeys.has(key.toString())
  const isFocused = state.selectionManager.focusedKey === key

  const ref = useRef(null)
  const { menuItemProps } = useMenuItem(
    {
      key,
      isDisabled,
      onAction,
      isSelected,
      onClose,
      closeOnSelect,
    },
    state,
    ref,
  )

  if (!Array.isArray(rendered) || !(typeof rendered[rendered.length - 1] === 'function')) return null

  const submenuFn: (close?: () => void) => React.ReactElement = rendered[rendered.length - 1]
  const itemContent = rendered.slice(0, rendered.length - 1)

  return (
    <FocusRing focusRingClass={Styles.focusRing}>
      <li {...menuItemProps} ref={ref} className={Styles.subMenuItem}>
        <SubmenuTrigger
          isOpen={isExpanded}
          placement="right-start"
          closeOnSelect
          onOpenChange={(isOpen) => {
            if (isOpen !== isExpanded) {
              state.expandedKeys.forEach((k) => {
                if (k !== key) {
                  state.toggleKey(k)
                }
              })
              state.toggleKey(key)
            }
          }}
        >
          <div
            className={cx(Styles.item, className, {
              [Styles.itemHovered]: isFocused || isExpanded,
              [Styles.itemSelected]: isSelected,
              [Styles.itemDisabled]: isDisabled,
            })}
          >
            <div className={Styles.itemContent}>{itemContent}</div>
            {isSelected && showCheckmark && <CheckIcon className={Styles.itemCheckIcon} />}
            <span className={Styles.chevron}>
              <ChevronRight />
            </span>
          </div>
          {submenuFn(onClose)}
        </SubmenuTrigger>
      </li>
    </FocusRing>
  )
}

export { SubmenuItem }
